import {DateTimeFormatter, Duration, Instant, LocalDate} from "@js-joda/core";
import {Drawer, Stack, TextField, Typography} from "@mui/material";
import React from "react";
import type {MilestoneEscorts} from "../../../firebase/index.js";
import {addMilestone, generateMainFirebaseRtdbKey, getDateTextFromMillis, getMillisFromDateText, Milestone} from "../../../firebase/index.js";
import APXActionButtons from "../../../reusables/APXActionButtons.jsx";
import type {BSLProps} from "../../../reusables/js/APXBottomSheetLayoutUtils.js";
import useBottomSheet from "../../../reusables/js/APXBottomSheetLayoutUtils.js";


export default function AddNewMilestoneBSL({uid, industryKey, m, e, onClose}: { m: Milestone, e: MilestoneEscorts, industryKey: string } & BSLProps) {
	  
	  const mForEdit: Milestone = m;
	  const escorts: MilestoneEscorts = e;
	  
	  React.useEffect(() => {
			 if (mForEdit) {
					setTitle(mForEdit.milestoneTitle);
					setDetails(mForEdit.milestoneDetails);
					setDateText(getDateTextFromMillis(mForEdit.milestoneMillis));
			 }
	  }, [mForEdit]);
	  
	  
	  const {isOpen, handleClose} = useBottomSheet({initialState: true, onClose});
	  
	  const [disablePositiveByDefault, setDisablePositiveByDefault] = React.useState(true);
	  
	  const [title, setTitle] = React.useState("");
	  const [details, setDetails] = React.useState("");
	  const [dateText, setDateText] = React.useState(escorts ? LocalDate.ofInstant(Instant.ofEpochMilli(escorts.prev.milestoneMillis)).plusDays(1).toString() : "");
	  
	  const [daysFromNow, setDaysFromNow] = React.useState("");
	  
	  React.useEffect(() => {
			 if (dateText) {
					const pickedLdt = LocalDate.parse(dateText).atStartOfDay();
					const daysFromNow = Duration.between(LocalDate.now().atStartOfDay(), pickedLdt).toDays();
					
					if (daysFromNow > 0) {
						  if (escorts) {
								 const prevLdt = LocalDate.ofInstant(Instant.ofEpochMilli(escorts.prev.milestoneMillis)).atStartOfDay();
								 const nextLdt = LocalDate.ofInstant(Instant.ofEpochMilli(escorts.next.milestoneMillis)).atStartOfDay();
								 const dtf = DateTimeFormatter.ofPattern("dd/MM/yyyy");
								 try { /* BECAUSE DATE TIME FORMATTER LOCALE ISSUE IS CONFUSING AND MAY THROW EXCEPTION */
										setDaysFromNow(
												  <>
														 {daysFromNow} day(s) from now
														 <br/>
														 {Duration.between(prevLdt, pickedLdt).toDays()} day(s) from previous milestone {prevLdt.format(dtf)}
														 <br/>
														 {Duration.between(pickedLdt, nextLdt).toDays()} day(s) to next milestone {nextLdt.format(dtf)}
												  </>
										);
								 } catch (e) {
										setDaysFromNow(daysFromNow + " day(s) from now");
								 }
						  } else {
								 setDaysFromNow(daysFromNow + " day(s) from now");
						  }
					} else {
						  setDaysFromNow("");
					}
			 } else {
					setDaysFromNow("");
			 }
	  }, [dateText]);
	  
	  
	  React.useEffect(() => {
			 setDisablePositiveByDefault(/*FALSY->*/ !!!title.trim() || /*FALSY->*/ !!!details.trim() || /*FALSY->*/ !!!dateText.trim());
	  }, [title, details, dateText]);
	  
	  const handleCancel = () => {
			 setTitle("");
			 setDetails("");
			 setDateText("");
			 setDaysFromNow("");
			 onClose();
	  };
	  
	  const handleAdd = () => {
			 const key = mForEdit ? mForEdit.milestoneKey : generateMainFirebaseRtdbKey();
			 const millis = getMillisFromDateText(dateText);
			 const milestone = new Milestone(key, title.trim(), details.trim(), millis);
			 addMilestone(uid, industryKey, key, milestone);
			 handleCancel();
	  };
	  
	  return (
				 <Drawer anchor="bottom" open={isOpen} onClose={handleClose} variant="temporary">
						<Stack spacing={2} p={2}>
							  <TextField label={"Title"} variant="outlined" fullWidth required
											 value={title}
											 onChange={(e) => setTitle(e.target.value)}
							  />
							  <TextField label={"Details"} variant="outlined" fullWidth multiline rows={2} required
											 value={details}
											 onChange={(e) => setDetails(e.target.value)}
							  />
							  <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={"center"} justifyContent={"center"}>
									 <TextField label={"Target"} variant="outlined" fullWidth required
													type="date"
													InputLabelProps={{shrink: true}}
													InputProps={escorts &&
															  {
																	 inputProps:
																				{
																					  min: LocalDate.ofInstant(Instant.ofEpochMilli(escorts.prev.milestoneMillis)).plusDays(1).toString(),
																					  max: LocalDate.ofInstant(Instant.ofEpochMilli(escorts.next.milestoneMillis)).minusDays(1).toString()
																				}
															  }
													}
													value={dateText}
													onChange={(e) => setDateText(e.target.value)}
									 />
									 <Typography variant="body2" align={"center"} width={"100%"} color="primary">{daysFromNow}</Typography>
							  </Stack>
							  
							  <APXActionButtons onCancel={handleCancel}
													  onAdd={handleAdd}
													  disablePositiveByDefault={disablePositiveByDefault}
							  />
						</Stack>
				 </Drawer>
	  );
}
